import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "../lib/i18n";
import hero1 from "../assets/hero-1.webp";
import hero2 from "../assets/hero-2.webp";
import hero3 from "../assets/hero-3.webp";
import setraHd1 from "../assets/setra415-hd-1.webp";
import crafter1 from "../assets/volkswagen-crafter-1.webp";
// padeka images (jpgs provided by user)
import padeka1 from "../assets/padeka-1.webp";
import padeka2 from "../assets/padeka-2.webp";
import padeka3 from "../assets/padeka-3.webp";
import padeka4 from "../assets/padeka-4.webp";
import padeka5 from "../assets/padeka-5.webp";
import padeka6 from "../assets/padeka-6.webp";
import useEmblaCarousel from "embla-carousel-react";
import { Dialog, DialogContent } from "../components/ui/dialog";
import FeaturesSection from "../components/FeaturesSection";
import { useSeo } from "../lib/seo";

export default function MainPage() {
  const { t, lang } = useTranslation();
  useSeo({ title: t("seoHomeTitle"), description: t("seoHomeDesc"), path: "/", lang });

  const aboutHighlights: Record<string, string[]> = {
    lt: [
      "keleivinio transporto paslaugų įmonė",
      "kokybiškas keleivių vežimo paslaugas Lietuvoje ir užsienyje",
      "aukštą paslaugų kokybę",
    ],
    en: [
      "passenger transportation services company",
      "quality passenger transportation services in Lithuania and abroad",
      "high service quality",
    ],
  };

  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const renderWithHighlights = (text: string, keyPrefix = "") => {
    const phrases = aboutHighlights[lang] ?? [];
    const escaped = phrases.map(escapeRegExp).filter(Boolean);
    if (escaped.length === 0) return text;
    const regex = new RegExp(`(${escaped.join("|")})`, "g");
    const parts = text.split(regex);
    return parts.map((part, idx) => {
      if (phrases.includes(part)) {
        return (
          <span key={`${keyPrefix}-${idx}`} className="text-red-600 font-semibold">
            {part}
          </span>
        );
      }
      return <React.Fragment key={`${keyPrefix}-${idx}`}>{part}</React.Fragment>;
    });
  };

  const heroImages = [hero1, hero2, hero3];
  const padekos = [padeka1, padeka2, padeka3, padeka4, padeka5, padeka6];

  const [heroRef, heroApi] = useEmblaCarousel({ loop: true, align: "center" });
  const [padekaRef, padekaApi] = useEmblaCarousel({ loop: true, align: "center" });
  const [padekaIndex, setPadekaIndex] = useState(0);
  const [selectedPadeka, setSelectedPadeka] = useState<string | null>(null);

  useEffect(() => {
    if (!heroApi) return;
    const interval = setInterval(() => {
      heroApi.scrollNext();
    }, 4000);
    return () => clearInterval(interval);
  }, [heroApi]);

  useEffect(() => {
    if (!padekaApi) return;
    const onSelect = () => setPadekaIndex(padekaApi.selectedScrollSnap());
    padekaApi.on("select", onSelect);
    onSelect();
    return () => {
      padekaApi.off("select", onSelect);
    };
  }, [padekaApi]);
  const padekaNext = useCallback(() => padekaApi && padekaApi.scrollNext(), [padekaApi]);
  const padekaPrev = useCallback(() => padekaApi && padekaApi.scrollPrev(), [padekaApi]);

  return (
    <div>
      {/* Hero carousel with static overlay text */}
      <section className="relative">
        <div className="embla" ref={heroRef}>
          <div className="embla__container">
            {heroImages.map((src, idx) => (
              <div className="embla__slide" key={idx}>
                <div className="hero-bg h-[420px]" style={{ backgroundImage: `url(${src})` }} />
              </div>
            ))}
          </div>
        </div>

        {/* Static overlay text that stays while background changes */}
        <div className="absolute inset-0 flex items-center pointer-events-none">
          <div className="max-w-7xl mx-auto px-6 pointer-events-auto">
            <div className="bg-sky-800/90 inline-block text-white px-4 py-2 rounded">
              <h1 className="text-xl md:text-3xl font-extrabold tracking-wide uppercase">{t("heroTitle")}</h1>
            </div>
            <p className="mt-4 text-white/90 max-w-2xl">{t("heroSubtitle")}</p>
          </div>
        </div>
      </section>

      <FeaturesSection />

      <section className="py-12">
        <h2 className="text-center text-xl font-semibold">{t("offerTitle")}</h2>
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8 mt-6 px-6">
          <div className="text-center">
            <img src={setraHd1} alt={t("buses")} className="mx-auto w-80 h-52 object-cover rounded shadow" />
            <h3 className="mt-4 font-semibold">{t("busesColumn")}</h3>
            <Link to="/buses" className="inline-block mt-4 px-4 py-2 bg-red-600 text-white rounded">
              {t("viewOffers")}
            </Link>
          </div>

          <div className="text-center">
            <img src={crafter1} alt={t("microbuses")} className="mx-auto w-80 h-52 object-cover rounded shadow" />
            <h3 className="mt-4 font-semibold">{t("microbusesColumn")}</h3>
            <Link to="/microbuses" className="inline-block mt-4 px-4 py-2 bg-red-600 text-white rounded">
              {t("viewOffers")}
            </Link>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white/5">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8 items-start px-6">
          <div>
            <h3 className="font-semibold">{t("aboutUsTitle")}</h3>
            {t("aboutUsText")
              .split("\n\n")
              .map((para, i) => (
                <p key={i} className="mt-4 text-sm text-foreground/90">
                  {renderWithHighlights(para, `about-${i}`)}
                </p>
              ))}
          </div>

          <div>
            <h3 className="font-semibold">{t("padekosTitle")}</h3>
            <p className="text-sm mt-1 text-foreground/80">{t("padekaCaption")}</p>

            <div className="mt-4">
              <div className="embla" ref={padekaRef}>
                <div className="embla__container">
                  {padekos.map((src, idx) => (
                    <div className="embla__slide" key={idx}>
                      <div className="relative">
                        <img
                          src={src}
                          alt={`${t("padekosTitle")} ${idx + 1}`}
                          className="w-full h-64 object-contain rounded shadow cursor-pointer"
                          onClick={() => setSelectedPadeka(src)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-center gap-4 mt-3">
                <button onClick={padekaPrev} className="embla__button" aria-label="Previous slide">‹</button>
                <div className="embla__dots">
                  {padekos.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => padekaApi && padekaApi.scrollTo(i)}
                      aria-label={`Go to slide ${i + 1}`}
                      aria-current={padekaIndex === i ? "true" : undefined}
                      className={`embla__dot ${padekaIndex === i ? "active" : ""}`}
                    />
                  ))}
                </div>
                <button onClick={padekaNext} className="embla__button" aria-label="Next slide">›</button>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Padeka lightbox dialog */}
      <Dialog open={!!selectedPadeka} onOpenChange={(open) => !open && setSelectedPadeka(null)}>
        <DialogContent className="max-w-4xl p-0 bg-transparent">
          {selectedPadeka && (
            <img src={selectedPadeka} alt={t("padekosTitle")} className="w-full max-h-[80vh] object-contain rounded" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
